import EmptyDottedIcon from '@assets/icons/empty-dotted.svg';
import i18n from '@/utils/i18n';
import NoInternetIcon from '@assets/icons/no-internet.svg';
import React, { useCallback } from 'react';
import { EmptyState } from '@experiences/components/empty-state';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { partnerSupplier } from '@/utils/environment';
import { ProspectsRepViewSkeleton } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-rep-view-skeleton';
import { ProspectsRepViewTable } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-rep-view-table/prospects-rep-view-table';
import { RepViewProspectRowData } from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalProspectsStackParams } from '@/navigators/types';
import { SEARCHBAR_MAX_WIDTH } from '@/experiences/sales-portal/domain/prospects/screens/prospects/constants';
import { SearchInput } from '@/experiences/sales-portal/components/search-input';
import { StackNavigationProp } from '@react-navigation/stack';
import { useFilteredRepViewProspects } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/hooks/use-filtered-rep-view-prospects';
import { useNavigation } from '@react-navigation/native';
import { useRepViewProspectsLoader } from '@/experiences/sales-portal/domain/prospects/screens/prospects/blocs/loaders/use-rep-view-prospects-loader';
import { useTheme } from '@/contexts/theme-context';
import { View } from '@usepepper/sprout-kit-ui';

export const ProspectsRepViewContent: React.FC = () => {
  const theme = useTheme();
  const navigation =
    useNavigation<StackNavigationProp<SalesPortalProspectsStackParams>>();

  const { error, loading, prospects, refetch } = useRepViewProspectsLoader({
    businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
  });

  const { deferredSearchText, filteredProspects, searchText, setSearchText } =
    useFilteredRepViewProspects(prospects);

  const handleRowClick = useCallback(
    (row: RepViewProspectRowData) => {
      navigation.push(SALES_PORTAL_ROUTES.ProspectDetail, {
        restaurantUUID: row.restaurantUUID,
        supplierUUID: row.supplierUUID,
      });
    },
    [navigation],
  );

  if (loading) {
    return <ProspectsRepViewSkeleton />;
  }

  if (error && prospects.length === 0) {
    return (
      <ErrorState
        buttonLabel={i18n.t('sales_portal.error.refresh')}
        description={i18n.t('sales_portal.error.description')}
        icon={<NoInternetIcon stroke={theme.sk.color.icon.primary} />}
        onPress={() => refetch()}
        title={i18n.t('sales_portal.error.title')}
      />
    );
  }

  return (
    <View flex={1} gap="$12" paddingTop="$24">
      {prospects.length > 0 ? (
        <View alignSelf="center" maxWidth={SEARCHBAR_MAX_WIDTH} width="100%">
          <SearchInput
            onSearchChange={setSearchText}
            placeholderText={i18n.t(
              'sales_portal.prospects.search_placeholder',
            )}
            searchText={searchText}
          />
        </View>
      ) : null}
      <ProspectsRepViewTable
        appliedSearchText={deferredSearchText}
        data={filteredProspects}
        emptyComponent={
          <EmptyState
            description={i18n.t(
              deferredSearchText
                ? 'sales_portal.prospects.search_empty.description'
                : 'sales_portal.prospects.empty.description',
            )}
            iconElement={
              <EmptyDottedIcon stroke={theme.sk.color.icon.primary} />
            }
            title={i18n.t(
              deferredSearchText
                ? 'sales_portal.prospects.search_empty.title'
                : 'sales_portal.prospects.empty.title',
              deferredSearchText
                ? { searchText: deferredSearchText }
                : undefined,
            )}
          />
        }
        onRowClick={handleRowClick}
      />
    </View>
  );
};
